<template>
    <!-- Desktop Mode (>= 768px): Normal Floating Draggable Notes -->
    <template v-if="!isMobile">
        <DraggableNotes v-for="note in userNotes" :key="note.id" :note="note" 
            @update:note="updateNotePosition"
            @startDrag="startDrag"
            @drag="drag"
            @endDrag="endDrag">
            <div v-resize 
                class="user-note absolute select-none w-[100px] w-fit h-fit z-[45] bg-[#ffff8f] border-[#dbdb76] border border-dashed rounded-md no-user-select max-w-[calc(100vw-32px)] shadow-md" 
                @stopResize="updateSize($event, note)"
                :style="{ left: note.x + 'px', top: note.y + 'px', width: (note.width || 200) + 'px', height: (note.height || 160) + 'px' }">
                <div class="flex flex-col justify-between p-2 w-full h-full">
                    <div class="flex flex-row justify-between items-center">
                        <Tooltip class="cursor-pointer" position="bottom" :text="$t('userNotes.move')">
                            <div class="handle active:cursor-grabbing">
                                <ArrowsPointingOutIcon class="size-6 text-black" />
                            </div>
                        </Tooltip>
                        <div class="flex flex-row gap-2 items-center justify-center">
                            <Tooltip class="cursor-pointer" position="bottom" :text="$t('userNotes.share')">
                                <button class="size-6 text-black cursor-pointer" @click="shareNote(note)">
                                    <ShareIcon class="size-6 text-black" />
                                </button>
                            </Tooltip>
                            <Tooltip class="cursor-pointer" position="bottom" :text="$t('userNotes.remove')">
                                <button class="cursor-pointer text-black hover:text-red-600 transition-colors" 
                                    @click="removeNote(note)">
                                    <XMarkIcon class="size-6" />
                                </button>
                            </Tooltip>
                        </div>
                    </div>
                    <h3 class="text-base text-black dark:text-slate-800 font-bold no-user-select bg-transparent border-none outline-none w-full h-full overflow-y-auto my-1"
                        contenteditable="true"
                        @blur="updateNote(note, 'note', $event.target.innerText)" v-html="(note.note || '').replace(/\n/g, '<br>')"></h3>
                    <div class="flex flex-col text-[8px] text-black/70 no-user-select mt-1">
                        <span>{{ formatDate(note.createdAt) }}</span>
                        <span>{{ formatDate(note.updatedAt) }}</span>
                    </div>
                    <div class="resizer resizer-right"></div>
                    <div class="resizer resizer-bottom"></div>
                    <div class="resizer resizer-corner"></div>
                </div>
            </div>
        </DraggableNotes>
    </template>

    <!-- Mobile Mode (< 768px): Discrete Mini-Badge / Floating Pin + Slide-Up Bottom Sheet -->
    <template v-else>
        <!-- Floating Mini-Badge (Pill) -->
        <Teleport to="body">
            <div v-if="userNotes.length > 0" class="fixed top-16 right-3 z-[60] animate-fade-in">
                <button 
                    @click="openBottomSheet(userNotes[0])"
                    type="button"
                    class="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-yellow-300 dark:bg-yellow-400 text-yellow-950 font-bold text-xs shadow-lg border border-yellow-400/80 active:scale-95 transition-all cursor-pointer"
                >
                    <DocumentTextIcon class="size-4 text-yellow-950 flex-shrink-0" />
                    <span>{{ userNotes.length === 1 ? $t('userNotes.singleNote', { count: 1 }) : $t('userNotes.multipleNotes', { count: userNotes.length }) }}</span>
                </button>
            </div>

            <!-- Slide-Up Bottom Sheet for Mobile -->
            <Transition name="fade">
                <div v-if="bottomSheetOpen" class="fixed inset-0 z-[300] bg-black/50 backdrop-blur-sm flex items-end justify-center" @click.self="closeBottomSheet">
                    <div class="w-full max-w-lg bg-white dark:bg-gray-800 rounded-t-2xl shadow-2xl p-4 flex flex-col max-h-[85vh] animate-slide-up">
                        <!-- Drawer Drag Handle Indicator -->
                        <div class="w-12 h-1.5 bg-gray-300 dark:bg-gray-600 rounded-full mx-auto mb-3"></div>

                        <!-- Header -->
                        <div class="flex items-center justify-between border-b border-gray-100 dark:border-gray-700 pb-3 mb-3">
                            <div class="flex items-center gap-2">
                                <DocumentTextIcon class="size-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
                                <h3 class="text-base font-bold text-gray-900 dark:text-white m-0">{{ $t('userNotes.title') }}</h3>
                            </div>
                            <div class="flex items-center gap-2">
                                <button 
                                    v-if="selectedNote"
                                    @click="shareNote(selectedNote)" 
                                    :title="$t('userNotes.share')" 
                                    class="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                >
                                    <ShareIcon class="size-5" />
                                </button>
                                <button 
                                    v-if="selectedNote"
                                    @click="removeSelectedNote" 
                                    :title="$t('userNotes.remove')" 
                                    class="p-1.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                                >
                                    <TrashIcon class="size-5" />
                                </button>
                                <button 
                                    @click="closeBottomSheet" 
                                    :title="$t('userNotes.close')"
                                    class="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                >
                                    <XMarkIcon class="size-6" />
                                </button>
                            </div>
                        </div>

                        <!-- Multi-notes Tabs if more than 1 note -->
                        <div v-if="userNotes.length > 1" class="flex gap-1.5 overflow-x-auto pb-2 mb-2 no-scrollbar">
                            <button 
                                v-for="(n, idx) in userNotes" 
                                :key="n.id" 
                                @click="selectNote(n)"
                                class="px-3 py-1 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors"
                                :class="selectedNote?.id === n.id ? 'bg-yellow-300 text-yellow-950 shadow-sm' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'"
                            >
                                {{ $t('userNotes.noteLabel', { index: idx + 1 }) }}
                            </button>
                        </div>

                        <!-- Sticky Note Content Card -->
                        <div v-if="selectedNote" class="flex-grow flex flex-col bg-[#ffff8f] dark:bg-[#fef08a] border border-[#eab308]/40 rounded-xl p-3.5 shadow-inner">
                            <textarea 
                                v-model="selectedNote.note" 
                                @input="onMobileNoteInput"
                                :placeholder="$t('userNotes.placeholder')"
                                class="w-full flex-grow bg-transparent border-none outline-none resize-none text-gray-950 font-medium text-base leading-relaxed p-0 focus:ring-0 min-h-[140px]"
                            ></textarea>
                            <div class="flex justify-between items-center text-[10px] text-gray-700 pt-2 border-t border-yellow-400/40 mt-2">
                                <span>{{ formatDate(selectedNote.updatedAt || selectedNote.createdAt) }}</span>
                                <span class="text-emerald-700 font-semibold flex items-center gap-1">
                                    <span class="size-1.5 rounded-full bg-emerald-500 inline-block"></span> {{ $t('userNotes.saved') }}
                                </span>
                            </div>
                        </div>

                        <!-- Footer Action (Add Note / Close) -->
                        <div class="flex gap-2 mt-4">
                            <button 
                                @click="createNoteFromMobile" 
                                class="flex-1 py-2.5 px-4 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-sm shadow-sm active:scale-95 transition-all flex items-center justify-center gap-1.5"
                            >
                                <span>{{ $t('userNotes.newNote') }}</span>
                            </button>
                            <button 
                                @click="closeBottomSheet" 
                                class="py-2.5 px-5 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 font-semibold text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                                {{ $t('userNotes.close') }}
                            </button>
                        </div>
                    </div>
                </div>
            </Transition>
        </Teleport>
    </template>
</template>

<script setup>
/* eslint-disable */
import { ref, defineProps, defineExpose, onMounted, onBeforeUnmount, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import DraggableNotes from './DraggableNotes.vue';
import { ArrowsPointingOutIcon, XMarkIcon, ShareIcon, TrashIcon, DocumentTextIcon } from '@heroicons/vue/24/outline';
import appStore from '@/store/app.store';
import userStore from '@/store/user.store';
import { debounce } from '../../../service/debounce';
import toastStore from '@/store/toast.store';
import dialogStore from '@/store/dialog.store';
import syncService from '@/service/sync.service';
import dbService from '@/service/db.service';
import { realtimeService } from '@/service/realtime.service';

const { t } = useI18n();

const props = defineProps({
    storeKey: {
        type: String,
        required: true
    },
    playlistId: {
        type: String,
        required: false
    },
    songId: {
        type: String,
        required: false
    }
});

const userNotes = ref([]);
const isMobile = ref(typeof window !== 'undefined' ? window.innerWidth < 768 : false);
const bottomSheetOpen = ref(false);
const selectedNote = ref(null);

const handleResize = () => {
    isMobile.value = window.innerWidth < 768;
};

const openBottomSheet = (note) => {
    selectedNote.value = note || userNotes.value[0] || null;
    bottomSheetOpen.value = true;
};

const closeBottomSheet = () => {
    bottomSheetOpen.value = false;
};

const selectNote = (note) => {
    selectedNote.value = note;
};

const removeSelectedNote = () => {
    if (!selectedNote.value) return;
    removeNote(selectedNote.value);
    if (userNotes.value.length > 0) {
        selectedNote.value = userNotes.value[0];
    } else {
        selectedNote.value = null;
        closeBottomSheet();
    }
};

const onMobileNoteInput = debounce(() => {
    if (!selectedNote.value) return;
    updateNote(selectedNote.value, 'note', selectedNote.value.note);
}, 300);

const createNoteFromMobile = () => {
    const newNote = {
        id: (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : Date.now().toString(),
        note: '',
        x: 20,
        y: 20,
        width: 220,
        height: 180,
        createdAt: new Date(),
        updatedAt: new Date()
    };
    addUserNotes(newNote);
    selectedNote.value = newNote;
};

const updateNote = (note, field, value) => {
    const noteIndex = userNotes.value.findIndex(n => n.id === note.id);
    if (noteIndex !== -1) {
        userNotes.value[noteIndex] = { ...userNotes.value[noteIndex], [field]: value, updatedAt: new Date() };
    }
    saveNotesOnLocalStorage(userNotes.value, true);
};

const updateNotePosition = (updatedNote) => {
    const noteIndex = userNotes.value.findIndex(n => n.id === updatedNote.id);
    if (noteIndex !== -1) {
        userNotes.value[noteIndex] = { ...userNotes.value[noteIndex], ...updatedNote };
    }
    saveNotesOnLocalStorage(userNotes.value, true);
};

const removeNote = (note) => {
    const noteIndex = userNotes.value.findIndex(n => n.id === note.id);
    if (noteIndex !== -1) {
        userNotes.value.splice(noteIndex, 1);
    }
    saveNotesOnLocalStorage(userNotes.value, true);
};

const formatDate = (date) => {
    if (!date) return '';
    try {
        return new Date(date).toLocaleDateString(undefined, { 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric', 
            hour: '2-digit', 
            minute: '2-digit' 
        });
    } catch (e) {
        return '';
    }
};

const startDrag = () => {
    appStore.state.disableEvents = true;
};
const drag = () => {
    appStore.state.disableEvents = true;
};
const endDrag = () => {
    appStore.state.disableEvents = false;
};

const saveNotesLocally = (notes) => {
    if (props.storeKey) {
        try {
            localStorage.setItem(props.storeKey, JSON.stringify(notes));
        } catch (e) {
            console.warn('[UserNotes] Error saving to localStorage (persisting in IndexedDB):', e);
        }
        dbService.setItem(props.storeKey, notes).catch(err => {
            console.warn('[UserNotes] Error saving to IndexedDB:', err);
        });
    }
};

const debouncedSaveUserNotes = debounce(() => {
    saveUserNotes();
}, 500);

const saveNotesOnLocalStorage = (notes, savedDb = false) => {
    saveNotesLocally(notes);
    if (savedDb) {
        debouncedSaveUserNotes();
    }
};

const getLegacyStoreKey = () => {
    if (props.playlistId && props.songId) {
        return 'notes-playlist-' + props.playlistId + '-song-' + props.songId;
    }
    return null;
};

const getNotesFromLocalStorage = () => {
    let notes = [];
    try {
        if (props.storeKey) {
            const stored = localStorage.getItem(props.storeKey);
            if (stored) {
                notes = JSON.parse(stored);
            }
        }
        if ((!notes || notes.length === 0) && getLegacyStoreKey()) {
            const legacyStored = localStorage.getItem(getLegacyStoreKey());
            if (legacyStored) {
                notes = JSON.parse(legacyStored);
                if (props.storeKey && Array.isArray(notes)) {
                    saveNotesLocally(notes);
                }
            }
        }
    } catch (e) {
        console.error(e);
    }
    if (navigator.onLine && props.playlistId && props.songId) {
        const notesUrl = '/api/playlist/' + props.playlistId + '/song/' + props.songId + '/notes';
        if (!syncService.hasPending(notesUrl)) {
            fetchUserNotes();
        }
    }
    return Array.isArray(notes) ? notes : [];
};

const fetchUserNotes = async () => {
    if (!props.playlistId || !props.songId || !navigator.onLine) {
        return;
    }
    const targetSongId = props.songId;
    const targetStoreKey = props.storeKey;
    const notesUrl = '/api/playlist/' + props.playlistId + '/song/' + targetSongId + '/notes';
    if (syncService.hasPending(notesUrl)) {
        return;
    }
    try {
        const response = await fetch(notesUrl, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        });
        if (!response.ok) return;
        const data = await response.json();
        if (props.songId !== targetSongId) return;
        if (syncService.hasPending(notesUrl)) return;
        if (Array.isArray(data)) {
            userNotes.value = data;
            if (targetStoreKey) {
                saveNotesLocally(data);
            }
        }
    } catch (err) {
        console.warn('[UserNotes] Error fetching notes from server:', err);
    }
};

const saveUserNotes = async () => {
    if (!props.playlistId || !props.songId) {
        console.warn('Playlist ID or song ID is required');
        return;
    }
    await fetch('/api/playlist/' + props.playlistId + '/song/' + props.songId + '/notes', {
        method: 'POST',
        body: JSON.stringify(userNotes.value),
        headers: {
            'Content-Type': 'application/json'
        }
    }).catch(console.warn);
};

const addUserNotes = (note, shared = false) => {
    const noteIndex = userNotes.value.findIndex(n => n.id === note.id);
    if (noteIndex !== -1) {
        userNotes.value[noteIndex] = note;
        toastStore.dispatch("addMessage", { message: t('userNotes.noteUpdated'), icon: 'success' });
        saveNotesOnLocalStorage(userNotes.value, true);
        return;
    }
    userNotes.value.push(note);
    if (shared) {
        toastStore.dispatch("addMessage", { message: t('userNotes.newNoteShared'), icon: 'success' });
    } 
    saveNotesOnLocalStorage(userNotes.value, true);
};

const updateSize = (event, note) => {
    note.width = event.detail.width;
    note.height = event.detail.height;
    const noteIndex = userNotes.value.findIndex(n => n.id === note.id);
    if (noteIndex !== -1) {
        userNotes.value[noteIndex] = { ...userNotes.value[noteIndex], ...note };
    }
    saveNotesOnLocalStorage(userNotes.value, true);
};

const shareNote = async (note) => {
    if (!appStore.state.enabledRealtime) {
        toastStore.dispatch("addMessage", { message: t('userNotes.realtimeDisabled') || 'Realtime updates disabled', icon: 'warning' });
        return;
    }
    if (await dialogStore.dispatch('confirm', t('userNotes.confirmShare'))) {
        fetch('/api/playlist/' + props.playlistId + '/song/' + props.songId + '/signal', {
            method: 'POST',
            body: JSON.stringify({
                type: 'share-note',
                target: note
            }),
            headers: {
                'Content-Type': 'application/json'
            }
        }).catch(console.warn);
        // Via transport dual (Ably + WebRTC): funciona em palco offline (sem backend).
        // Receptores aplicam via addUserNotes (dedup por id); sender é ignorado no onEvent.
        try {
            const cUid = userStore.state.userInfo?.id || userStore.state.userInfo?._id;
            realtimeService.publish(
                `playlist::room::${props.playlistId}::${props.songId}`,
                'share-note',
                { note, sender: cUid, sender_client_id: window.myClientId || null }
            ).catch(() => {});
        } catch (e) {}
    }
};

const loadNotes = async () => {
    const local = getNotesFromLocalStorage();
    if (local && local.length > 0) {
        userNotes.value = local;
        return;
    }
    if (props.storeKey) {
        try {
            let idbNotes = await dbService.getItem(props.storeKey);
            if (!idbNotes && getLegacyStoreKey()) {
                idbNotes = await dbService.getItem(getLegacyStoreKey());
                if (Array.isArray(idbNotes) && idbNotes.length > 0) {
                    dbService.setItem(props.storeKey, idbNotes);
                }
            }
            if (Array.isArray(idbNotes) && idbNotes.length > 0) {
                userNotes.value = idbNotes;
                return;
            }
        } catch (e) {
            console.warn('[UserNotes] Error loading from IndexedDB:', e);
        }
    }
    userNotes.value = [];
};

watch([() => props.songId, () => props.storeKey], ([newSongId, newStoreKey], [oldSongId, oldStoreKey]) => {
    if (newSongId !== oldSongId || newStoreKey !== oldStoreKey) {
        loadNotes();
    }
});

onMounted(() => {
    loadNotes();
    if (typeof window !== 'undefined') {
        window.addEventListener('resize', handleResize);
    }
});

onBeforeUnmount(() => {
    if (typeof window !== 'undefined') {
        window.removeEventListener('resize', handleResize);
    }
});

defineExpose({
    addNotes: (note, shared = false) => {
        addUserNotes(note, shared);
    },
    openNotesMobile: () => {
        if (userNotes.value.length > 0) {
            openBottomSheet(userNotes.value[0]);
        } else {
            createNoteFromMobile();
            openBottomSheet();
        }
    }
});
</script>

<style scoped>
@keyframes slideUp {
    from { transform: translateY(100%); }
    to { transform: translateY(0); }
}
.animate-slide-up {
    animation: slideUp 0.25s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}
.no-scrollbar::-webkit-scrollbar {
    display: none;
}
.no-scrollbar {
    -ms-overflow-style: none;
    scrollbar-width: none;
}
</style>
